import { useState, useEffect, useMemo } from "react";
import { v4 as uuidV4 } from "uuid";
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { genIdOptionList, genFlatOptionList, CommonDestinationTimezone } from "../../lib/utils";
import { Trash2 } from "lucide-react";
import { CommodityOptions, Commodities } from "../../lib/api/commodity";
import { Uom, Location, uomOptions, generateContractTypesWith } from "../../lib/api/generalContract";
import { formulateOptionMonthsWithOlder } from "../../lib/api/common";
import { Trader, Customer } from "../../lib/api/reports";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { getUserStytchName } from "../../lib/models/auth";
import { DirectSale, DirectSaleUpdateValidation, DirectSaleCreateValidation, useCreateDirectSales, useUpdateDirectSales } from "@/lib/api/directSales";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { safeParseDecimal } from "@/lib/excel";
import Decimal from "decimal.js";
import { ZodSchema } from "zod";
import { BearDatePicker, BearFloatInput } from "@/lib/form/ui";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Returns true if the value is present and non-empty after trimming.
 * Handles undefined, null, empty string, and whitespace-only strings.
 */
function hasValue(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
}

/**
 * Returns true if the line item is a legacy item that was created with
 * average_cash_price. These line items are frozen —
 * all inputs disabled and original values preserved.
 */
function isLegacyAvgCashPriceLineItem(lineItem: Record<string, unknown>): boolean {
  return hasValue(lineItem.average_cash_price);
}

function generateDefaultLineItem() {
    return {
        id: uuidV4(),
        qty: "",
        converted_qty: "",
        futures: "",
        basis: "",
        net_cash_price: "",
        marketing_fee: "",
        marketing_fee_rate: "",
    };
}

export function CreateDynamicForm({
    onCloseForm,
    traders,
    customers,
    locations,
}: {
    onCloseForm: any;
    locations: Location[];
    customers: Customer[];
    traders: Trader[];
}) {
    const createApiContract = useCreateDirectSales();
    const { member } = useStytchMember();
    const member_name = getUserStytchName(member);
    const [data, setData] = useState<DirectSale>({} as any);

    useEffect(() => {
        const trader_id = traders.find((trader: Trader) => trader.name === member_name)?.id;
        setData({
            contract_date: moment.tz(CommonDestinationTimezone).toDate(),
            trader_id: trader_id || "",
            uom: Uom.Bushel,
            contract_id: "Direct Sale",
            marketing_fee_rate: new Decimal("1.5"),
            LineItems: [generateDefaultLineItem() as any],
        } as any)
    }, [])

    return (
        <BasicDirectSaleForm
            id={"CreateDirectSales"}
            data={data}
            isUpdateForm={false}
            externalReferences={{
                locations,
                locationOptions: genIdOptionList(locations, "long_name"),
                customerOptions: genIdOptionList(customers, "long_name"),
                commodityOptions: CommodityOptions,
                uomOptions,
                traderOptions: genIdOptionList(traders, "name"),
                optionMonthOptions: formulateOptionMonthsWithOlder(),
            }}
            failMessage="Failed to create Direct Sale"
            successMessage="Created"
            updateFormValue={(key: string, value: any) => {
                setData({ ...data, [key]: value });
            }}
            updateFormValues={(values: Record<string, any>) => {
                setData({ ...data, ...values });
            }}
            onSubmitTo={createApiContract}
            onCloseForm={onCloseForm}
            Validator={DirectSaleCreateValidation}
        />
    );
}

export function UpdateDynamicForm({
    onCloseForm,
    traders,
    customers,
    locations,
    updatedFormData,
}: {
    onCloseForm: any;
    locations: Location[];
    customers: Customer[];
    traders: Trader[];
    updatedFormData: DirectSale | null;
}) {
    const updateApiContract = useUpdateDirectSales();
    const { member } = useStytchMember();
    const member_name = getUserStytchName(member);
    const [data, setData] = useState<DirectSale>({} as any);

    useEffect(() => {
        if (updatedFormData) {
            // Check if its pre migration multi row data
            if (!updatedFormData.LineItems?.length) {
                const LineItems: any[] = [{
                    ...generateDefaultLineItem(),
                    direct_sale_id: updatedFormData.id,
                    qty: updatedFormData.qty,
                    uom: updatedFormData.uom,
                    futures: updatedFormData.futures,
                    basis: updatedFormData.basis,
                    average_cash_price: updatedFormData.average_cash_price,
                    net_cash_price: updatedFormData.net_cash_price,
                    marketing_fee: updatedFormData.marketing_fee,
                    marketing_fee_rate: updatedFormData.marketing_fee_rate,
                }];

                setData({
                    ...updatedFormData,
                    LineItems
                });
            } else {
                setData({
                    ...updatedFormData,
                });
            }
        } else {
            setData({} as any);
        }

    }, [updatedFormData])

    return (
        <BasicDirectSaleForm
            id={"UpdateDirectSales"}
            data={data}
            isUpdateForm={true}
            externalReferences={{
                locations,
                locationOptions: genIdOptionList(locations, "long_name"),
                customerOptions: genIdOptionList(customers, "long_name"),
                commodityOptions: CommodityOptions,
                uomOptions,
                traderOptions: genIdOptionList(traders, "name"),
                optionMonthOptions: formulateOptionMonthsWithOlder(),
                contractTypeOptions: genFlatOptionList(generateContractTypesWith({})),
            }}
            failMessage="Failed to update Direct Sale"
            successMessage="Updated"
            updateFormValue={(key: string, value: any) => {
                setData({ ...data, [key]: value });
            }}
            updateFormValues={(values: Record<string, any>) => {
                setData({ ...data, ...values });
            }}
            onSubmitTo={updateApiContract}
            onCloseForm={onCloseForm}
            Validator={DirectSaleUpdateValidation}
        />
    );
}

function BasicDirectSaleForm({
    id, data: base_data, onSubmitTo, isUpdateForm, externalReferences, updateFormValue, updateFormValues, onCloseForm, successMessage, failMessage,
    Validator
}: {
    id: string, data: Partial<DirectSale>, onSubmitTo: (data: any) => any, isUpdateForm: boolean, externalReferences: any, updateFormValue: (key: string, value: any) => void, updateFormValues: (values: Record<string, any>) => void, onCloseForm: (data: any) => void, successMessage: string, failMessage: string,
    Validator: ZodSchema
}) {
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lineItemErrors, setLineItemErrors] = useState<Record<number, string>>({});

    const onSubmit = async (data: any) => {
        try {
            if (isSubmitting) { return; }
            setIsSubmitting(true);
            
            // Clear previous errors
            setLineItemErrors({});
            const errors: Record<number, string> = {};

            // Validate line items for futures/basis logic
            for (let i = 0; i < data.LineItems.length; i++) {
                const lineItem = data.LineItems[i];

                // Legacy line items with average_cash_price skip validation
                if (isLegacyAvgCashPriceLineItem(lineItem)) {
                    continue;
                }

                const hasFutures = hasValue(lineItem.futures) && !safeParseDecimal(lineItem.futures).isZero();
                const hasBasis = hasValue(lineItem.basis);

                // Basis is always required
                if (!hasBasis) {
                    errors[i] = "Basis is required";
                    continue;
                }

                // If futures is provided, basis is also required (already checked above)
                // But futures cannot be zero
                if (hasFutures && safeParseDecimal(lineItem.futures).isZero()) {
                    errors[i] = "Futures cannot be zero";
                    continue;
                }
            }

            if (Object.keys(errors).length > 0) {
                setLineItemErrors(errors);
                setIsSubmitting(false);
                return;
            }

            const validatorResult = Validator.safeParse(JSON.parse(JSON.stringify(data)));

            if (!validatorResult.success) {
                toast({
                    title: "Invalid Data",
                    description: "",
                });
                setIsSubmitting(false);
                return;
            }

            // Validate delivery dates
            if (data.delivery_start_date && data.delivery_end_date) {
                const startDate = new Date(data.delivery_start_date);
                const endDate = new Date(data.delivery_end_date);
                if (endDate <= startDate) {
                    toast({
                        title: "Invalid Delivery Dates",
                        description: "Delivery end date must be after delivery start date",
                    });
                    setIsSubmitting(false);
                    return;
                }
            }

            const res = await onSubmitTo(data);
            if (!res.success) {
                throw res;
            }

            toast({
                title: successMessage,
                description: "",
            });
            onCloseForm(false);
        } catch (error) {
            toast({
                title: failMessage,
                description: "",
            });
            console.error("Failed to submit form", error);
        } finally {
            setIsSubmitting(false);
        }
    }

    const updateLineItems = (index: number, value: Record<string, any>) => {
        const newLineItems = [...(data.LineItems || [])];
        newLineItems[index] = { ...newLineItems[index], ...value };
        updateFormValue("LineItems", newLineItems);
    };

    const addLineItem = () => {
        const newLineItems = [...(data.LineItems || [])];
        newLineItems.push(generateDefaultLineItem() as any);
        updateFormValue("LineItems", newLineItems);
    };

    const removeLineItem = (index: number) => {
        if (data.LineItems?.length === 1) {
            return;
        }

        const newLineItems = [...(data.LineItems || [])];
        newLineItems.splice(index, 1);
        updateFormValue("LineItems", newLineItems);
    };

    const data = useMemo(() => {
        const uom = base_data.uom;
        const marketing_fee_rate = safeParseDecimal(base_data.marketing_fee_rate || 1.5);

        const LineItems = (base_data.LineItems || []).map((lineItem) => {
            const isLegacy = isLegacyAvgCashPriceLineItem(lineItem as unknown as Record<string, unknown>);

            // Legacy line items: preserve original values, do not recompute
            if (isLegacy) {
                return {
                    ...lineItem,
                    marketing_fee_rate,
                    uom,
                    has_placeholder_data: lineItem.has_placeholder_data ?? false,
                }
            }

            const hasFutures = hasValue(lineItem.futures);
            const hasBasis = hasValue(lineItem.basis);
            const has_placeholder_data = !hasFutures;
            const net_cash_price = (hasFutures && hasBasis)
                ? safeParseDecimal(lineItem.futures).plus(safeParseDecimal(lineItem.basis)).toFixed(2)
                : "0";
            const marketing_fee = (marketing_fee_rate).div(100).times(safeParseDecimal(net_cash_price).times(safeParseDecimal(lineItem.qty || 0))).toFixed(2);

            return {
                ...lineItem,
                net_cash_price,
                marketing_fee_rate,
                marketing_fee,
                uom,
                has_placeholder_data,
            }
        })

        const qty = LineItems.reduce((acc, lineItem) => {
            return acc.plus(safeParseDecimal(lineItem.qty || 0));
        }, new Decimal(0));

        const total_net_cash_price = LineItems.reduce((acc, lineItem) => {
            return acc.plus(safeParseDecimal(lineItem.net_cash_price || 0));
        }, new Decimal(0));

        const total_marketing_fee = LineItems.reduce((acc, lineItem) => {
            return acc.plus(safeParseDecimal(lineItem.marketing_fee || 0));
        }, new Decimal(0));


        return {
            ...base_data,
            notes: base_data.notes,
            uom,
            qty,
            crop_year: safeParseDecimal(base_data.crop_year || 0).toNumber(),
            contract_type: "direct_sale",
            net_cash_price: total_net_cash_price,
            marketing_fee: total_marketing_fee,
            LineItems,
        }
    }, [base_data]);

    return (
        <form
          className=" overflow-y-auto"
            onSubmit={(e) => {
                if (e?.preventDefault) e.preventDefault();

                return onSubmit(data);
            }}
        >
            <div className="flex flex-col gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
                <div className="flex flex-col gap-4 md:grid md:grid-cols-2 lg:grid-cols-4 lg:col-span-4">
                    <div className="grid items-center gap-1.5">
                        <BearDatePicker
                            required
                            name="gl_date"
                            label="GL Date"
                            value={data.gl_date || "" as any}
                            onValueChange={(value) => {
                                updateFormValue("gl_date", value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <BearDatePicker
                            required
                            name="contract_date"
                            label="Entry Date"
                            value={data.contract_date || "" as any}
                            onValueChange={(value) => {
                                updateFormValue("contract_date", value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="location_id" className="flex flex-row justify-between text-base items-center font-semibold">Location</Label>
                        <Combobox
                            virtualizeList
                            popoverClassName="w-[400px] h-[200px]"
                            isModal
                            dwimOnChange
                            required
                            className="w-full"
                            options={externalReferences.locationOptions}
                            value={data.location_id || ""}
                            onValueChange={(value: string) => {
                                updateFormValue("location_id", value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="producer_id" className="flex flex-row justify-between text-base items-center font-semibold">Producer</Label>
                        <Combobox
                            virtualizeList
                            popoverClassName="w-[400px] h-[200px]"
                            isModal
                            dwimOnChange
                            required
                            className="w-full"
                            options={externalReferences.customerOptions}
                            value={data.producer_id || ""}
                            onValueChange={(value: string) => {
                                updateFormValue("producer_id", value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="commodity" className="flex flex-row justify-between text-base items-center font-semibold">Commodity</Label>
                        <Combobox
                            popoverClassName="w-[400px] h-[200px]"
                            isModal
                            dwimOnChange
                            required
                            className="w-full"
                            options={externalReferences.commodityOptions}
                            value={data.commodity || ""}
                            onValueChange={(value: string) => {
                                updateFormValues({
                                    commodity: value,
                                    uom: value === Commodities.Canola ? Uom.Lbs : Uom.Bushel,
                                })
                            }}
                        />
                    </div>

                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="uom" className="flex flex-row justify-between text-base items-center font-semibold">UOM</Label>
                        <Combobox
                            popoverClassName="w-[400px] h-[200px]"
                            isModal
                            dwimOnChange
                            className="w-full"
                            required
                            options={externalReferences.uomOptions}
                            value={data.uom || ""}
                            onValueChange={(value: string) => {
                                updateFormValue("uom", value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="crop_year" className="flex flex-row justify-between text-base items-center font-semibold">Crop Year</Label>
                        <Input
                            required
                            type="number"
                            value={data.crop_year || ""}
                            min={2020}
                            max={2080}
                            onChange={(event) => {
                                updateFormValue("crop_year", event.target.value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="contract_id" className="flex flex-row justify-between text-base items-center font-semibold">Contract ID</Label>
                        <Input
                            required
                            type="text"
                            value={data.contract_id || ""}
                            onChange={(event) => {
                                updateFormValue("contract_id", event.target.value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="destination" className="flex flex-row justify-between text-base items-center font-semibold">Destination</Label>
                        <Input
                            required
                            type="text"
                            value={data.destination || ""}
                            onChange={(event) => {
                                updateFormValue("destination", event.target.value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="trader_id" className="flex flex-row justify-between text-base items-center font-semibold">Trader</Label>
                        <Combobox
                            isModal
                            dwimOnChange
                            required
                            className="w-full"
                            options={externalReferences.traderOptions}
                            value={data.trader_id || ""}
                            onValueChange={(value: string) => {
                                updateFormValue("trader_id", value);
                            }}
                        />
                    </div>
 
                    <div className="grid items-center gap-1.5">
                        <BearFloatInput
                            required
                            name={`marketing_fee_rate`}
                            label="Marketing Fee Rate"
                            value={(data as any).marketing_fee_rate?.toString() || ""}
                            onChange={(value) => {
                                updateFormValue("marketing_fee_rate", value);
                            }}
                        />
                    </div>

                    <div className="grid items-center gap-1.5">
                        <BearFloatInput
                            disabled
                            required
                            name={`marketing_fee_total`}
                            label="Marketing Fee Total"
                            value={(data as any).marketing_fee?.toString() || ""}
                            onChange={(value) => {
                                updateFormValue("marketing_fee", value);
                            }}
                        />
                    </div>


                    <div className="grid items-center gap-1.5">
                        <Label htmlFor="futures_month" className="flex flex-row justify-between text-base items-center font-semibold">Futures Month</Label>
                        <Combobox
                            isModal
                            dwimOnChange
                            className="w-full"
                            options={externalReferences.optionMonthOptions}
                            value={data.futures_month || ""}
                            onValueChange={(value: string) => {
                                updateFormValue("futures_month", value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <BearDatePicker
                            name="delivery_start_date"
                            label="Delivery Start Date"
                            value={data.delivery_start_date || "" as any}
                            onValueChange={(value) => {
                                updateFormValue("delivery_start_date", value);
                            }}
                        />
                    </div>
                    <div className="grid items-center gap-1.5">
                        <BearDatePicker
                            name="delivery_end_date"
                            label="Delivery End Date"
                            value={data.delivery_end_date || "" as any}
                            onValueChange={(value) => {
                                updateFormValue("delivery_end_date", value);
                            }}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="offset_against_ap_settlement"
                                checked={data.offset_against_ap_settlement || false}
                                onCheckedChange={(value) => {
                                    updateFormValue("offset_against_ap_settlement", value);
                                }}
                            />
                            <Label
                                htmlFor="offset_against_ap_settlement"
                                className="text-sm font-normal cursor-pointer"
                            >
                                Offset Against AP Settlement
                            </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="should_send_ar_invoice"
                                checked={data.should_send_ar_invoice || false}
                                onCheckedChange={(value) => {
                                    updateFormValue("should_send_ar_invoice", value);
                                }}
                            />
                            <Label
                                htmlFor="should_send_ar_invoice"
                                className="text-sm font-normal cursor-pointer"
                            >
                                Should Send AR Invoice
                            </Label>
                        </div>
                    </div>

                    <div className="grid items-center gap-1.5 lg:col-span-3">
                        <Label htmlFor="notes" className="flex flex-row justify-between text-base items-center font-semibold">Notes</Label>
                        <Input
                            type="text"
                            value={data.notes || ""}
                            onChange={(event) => {
                                updateFormValue("notes", event.target.value);
                            }}
                        />
                    </div>

                    <div className="flex flex-row justify-end gap-2 my-2">
                        <Button type="button" variant="outline" onClick={() => {
                            onCloseForm(false);
                        }}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                    </div>

                <Separator className="my-2 col-span-4" />


                 <div className="flex flex-row justify-between items-center lg:col-span-4">
                        <h3 className="text-lg font-semibold">Line Items</h3>
                        <span>
                            <Button type="button" variant="outline" size="sm" onClick={() => { addLineItem(); }}>
                                Add Line Item
                            </Button>
                        </span>
                    </div>
                <div className="flex flex-col gap-1 lg:col-span-4">

                       <div className="flex flex-col gap-1 lg:grid lg:grid-cols-1">
                        {data.LineItems?.map((lineItem, index) => {
                            const hasError = !!lineItemErrors[index];
                            const isLegacy = isLegacyAvgCashPriceLineItem(lineItem as unknown as Record<string, unknown>);
                            
                            return (
                                <div key={`line_item_${lineItem.id}`} className={cn(
                                    "flex flex-col gap-1 border rounded-lg p-4",
                                    hasError && "border-red-500 border-2",
                                    isLegacy && "bg-muted/50"
                                )}>
                                    {hasError && (
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                                            {lineItemErrors[index]}
                                        </div>
                                    )}

                                    {isLegacy && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                                            Legacy line item — uses average cash price. Values are read-only.
                                        </div>
                                    )}
                                    
                                    <div className="flex flex-col gap-4 md:grid md:grid-cols-4 lg:grid-cols-6">
                                        <div className="grid items-center gap-1.5 md:col-span-1">
                                            <BearFloatInput
                                                required
                                                disabled={isLegacy}
                                                name={`line_items.${lineItem.id}.qty`}
                                                label="Qty"
                                                value={lineItem.qty?.toString() || ""}
                                                onChange={(value) => {
                                                    updateLineItems(index, { qty: value });
                                                }}
                                            />
                                        </div>
                                        <div className="flex flex-col md:flex-row items-center gap-1.5 md:col-span-3 lg:col-span-2">
                                            <BearFloatInput
                                                disabled={isLegacy}
                                                name={`line_items.${lineItem.id}.futures`}
                                                label="Futures"
                                                value={lineItem.futures?.toString() || ""}
                                                onChange={(value) => {
                                                    updateLineItems(index, { futures: value });
                                                    // Clear error when user makes changes
                                                    if (lineItemErrors[index]) {
                                                        const newErrors = { ...lineItemErrors };
                                                        delete newErrors[index];
                                                        setLineItemErrors(newErrors);
                                                    }
                                                }}
                                            />
                                            <BearFloatInput
                                                disabled={isLegacy}
                                                name={`line_items.${lineItem.id}.basis`}
                                                label="Basis"
                                                value={lineItem.basis?.toString() || ""}
                                                onChange={(value) => {
                                                    updateLineItems(index, { basis: value });
                                                    // Clear error when user makes changes
                                                    if (lineItemErrors[index]) {
                                                        const newErrors = { ...lineItemErrors };
                                                        delete newErrors[index];
                                                        setLineItemErrors(newErrors);
                                                    }
                                                }}
                                            />
                                        </div>
                                      <div className="flex flex-col gap-4 lg:grid md:grid-cols-2 lg:grid-cols-3 md:col-span-1 lg:col-span-3">
                                        <div className="grid items-center gap-1.5">
                                            <BearFloatInput
                                                disabled
                                                name={`line_items.${lineItem.id}.net_cash_price`}
                                                label="Net Cash Price"
                                                value={lineItem.net_cash_price?.toString() || ""}
                                                onChange={(value) => {
                                                    updateLineItems(index, { net_cash_price: value });
                                                }}
                                            />
                                        </div>

                                        <div className="grid items-center gap-1.5">
                                            <BearFloatInput
                                                disabled
                                                name={`line_items.${lineItem.id}.marketing_fee_`}
                                                label="Marketing Fee"
                                                value={lineItem.marketing_fee?.toString() || ""}
                                                onChange={(value) => {
                                                    updateLineItems(index, { marketing_fee: value });
                                                }}
                                            />
                                        </div>
                                    <div className="flex flex-col justify-between items-center">
                                        <span className="flex flex-row justify-between items-center">
                                        <h4 className="text-sm font-semibold">Line Item {index + 1}</h4>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                removeLineItem(index);
                                                // Clear error for this item
                                                const newErrors = { ...lineItemErrors };
                                                delete newErrors[index];
                                                setLineItemErrors(newErrors);
                                            }}
                                            disabled={data.LineItems?.length === 1}
                                            className={cn(data.LineItems?.length === 1 && "opacity-50 cursor-not-allowed")}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </span>
                                    </div>
                                    </div>

                                    </div>
                                               </div>
                            );
                        })}
                    </div>
                </div>

                </div>
            </div>
        </form >
    );
}
